import React, { useEffect, useState } from 'react';
import Select from 'react-select';
import styled from 'styled-components';

import { configureDepartures, mapOptionToArray } from './helpers';
import { useFetchDepartures } from '../Cigarette/hooks';
import { useFetchDestinationIds } from './hooks';

import { WidgetName } from '../../models';
import {
  PACKAGE_WIDGET_SETTINGS,
  HOTEL_WIDGET_SETTINGS,
  CIGARETTE_WIDGET_SETTINGS,
  DIRECTION_WIDGET_SETTINGS,
  SEARCH_WIDGET_SETTINGS,
  METAREGIONS_WIDGET_SETTINGS,
} from '../../context/constructorContext/constants';

import UtmParamsControl from './components/UtmParamsControl';
import CheckboxControl from './components/CheckboxControl';
import TextControl from './components/TextControl';

import type {
  BaseWidgetSettingsControl,
  WidgetSettingsConstructorParams,
  WidgetSettingsControl,
} from '../../context/constructorContext/types';
import type { DepartureOption } from '@lt/api/dist/types/departure';

interface IWidgetConstructor {
  config: Partial<WidgetSettingsConstructorParams>;
  onWidgetSettingsChange: (newConfig: Partial<WidgetSettingsConstructorParams>) => void;
}

export const WidgetConstructor = (props: IWidgetConstructor): JSX.Element => {
  const { config, onWidgetSettingsChange } = props;
  const [newUtmParams, setNewUtmParams] = useState(Object.entries(config.utmParams?.value || {}));
  const { departures } = useFetchDepartures();
  const { destination_ids } = useFetchDestinationIds();

  if (config.departure?.value && !config.departure?.options && departures) {
    onWidgetSettingsChange({ ...config, departure: configureDepartures(config, departures) });
  }

  useEffect(() => {
    setNewUtmParams(Object.entries(config.utmParams?.value || {}));
  }, [config.selectedWidget, config.utmParams?.value]);

  useEffect(() => {
    if (!departures) {
      return;
    }

    onWidgetSettingsChange({ ...config, departure: configureDepartures(config, departures) });
  }, [config.search_type]);

  useEffect(() => {
    if (
      !destination_ids ||
      !config.selectedWidget?.value ||
      ![WidgetName.DIRECTION, WidgetName.METAREGIONS, WidgetName.SEARCH].includes(
        config.selectedWidget.value
      )
    ) {
      return;
    }

    onWidgetSettingsChange({ ...config, destination_ids });
  }, [config.selectedWidget?.value, destination_ids]);

  const selectWidget = (newWidgetName?: string) => {
    switch (newWidgetName) {
      case WidgetName.HOTEL:
        onWidgetSettingsChange(HOTEL_WIDGET_SETTINGS);
        break;
      case WidgetName.CIGARETTE:
        onWidgetSettingsChange(CIGARETTE_WIDGET_SETTINGS);
        break;
      case WidgetName.DIRECTION:
        onWidgetSettingsChange(DIRECTION_WIDGET_SETTINGS);
        break;
      case WidgetName.SEARCH:
        onWidgetSettingsChange(SEARCH_WIDGET_SETTINGS);
        break;
      case WidgetName.METAREGIONS:
        onWidgetSettingsChange(METAREGIONS_WIDGET_SETTINGS);
        break;
      case WidgetName.PACKAGE:
        onWidgetSettingsChange(PACKAGE_WIDGET_SETTINGS);
        break;
      default:
        onWidgetSettingsChange(CIGARETTE_WIDGET_SETTINGS);
    }
  };

  const applySettings = (
    controlName: string,
    controlValue: BaseWidgetSettingsControl<unknown>,
    newValues: Partial<WidgetSettingsControl<unknown>>
  ) => {
    const newConfig = {
      ...config,
      [controlName]: Array.isArray(newValues) ? newValues : { ...controlValue, ...newValues },
    };
    onWidgetSettingsChange(newConfig);
  };

  return (
    <Container>
      <h2>Настройка виджета</h2>
      <ControlsContainer>
        {Object.entries(config).map(([controlName, controlValue]) => {
          if (!controlValue) {
            return null;
          }
          const options = controlValue['options']
            ? mapOptionToArray<string | number | DepartureOption | undefined>(
                controlValue['options']
              )
            : undefined;

          if (!('label' in controlValue && 'description' in controlValue)) {
            return null;
          }

          return (
            <ControlContainer key={controlName}>
              <h4>{controlValue.label}</h4>
              <p>{controlValue.description}</p>
              {options ? (
                <StyledSelect
                  isMulti={
                    controlName === 'destination_ids' &&
                    config.selectedWidget?.value !== WidgetName.SEARCH
                  }
                  options={options}
                  value={
                    options.filter((option) =>
                      Array.isArray(controlValue.value)
                        ? controlValue.value.includes(option.value)
                        : typeof option?.value === 'object' && 'id' in option.value
                        ? option.value.id === controlValue.value.id
                        : option.value === controlValue.value
                    ) || ''
                  }
                  onChange={(newInputValue: WidgetSettingsControl<unknown>) => {
                    if (controlName === 'selectedWidget') {
                      selectWidget(newInputValue.value as WidgetName);
                      return;
                    }

                    const newValue = Array.isArray(newInputValue)
                      ? newInputValue.map((item) => item.value)
                      : newInputValue.value;

                    applySettings(controlName, controlValue, {
                      value: newValue,
                    });
                  }}
                />
              ) : controlName === 'utmParams' ? (
                <UtmParamsControl
                  controlName={controlName}
                  newUtmParams={newUtmParams}
                  setNewUtmParams={setNewUtmParams}
                  config={config}
                  onWidgetSettingsChange={onWidgetSettingsChange}
                />
              ) : typeof controlValue.value === 'boolean' ? (
                <CheckboxControl
                  controlName={controlName}
                  controlValue={controlValue}
                  applySettings={applySettings}
                />
              ) : (
                <TextControl
                  controlName={controlName}
                  controlValue={controlValue as BaseWidgetSettingsControl<string | number>}
                  applySettings={applySettings}
                />
              )}
            </ControlContainer>
          );
        })}
      </ControlsContainer>
    </Container>
  );
};

const Container = styled.div`
  width: 300px;
  min-width: 300px;
`;

const ControlsContainer = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: 5px;
`;

const ControlContainer = styled.div`
  margin-top: 10px;
`;

const StyledSelect = styled(Select)`
  // повышаем specificity чтобы при многократном использовании компонентов,
  // в разных виджетах на одной странице,
  // первичные стили не перетирали переопределенные
  && {
    width: 100%;
  }
`;
